import { channelsForTriggerVariables, destinationVariablesFor, nodeTypeRunsOn, spansOneChannelChoice, startsReaching, } from './channel-capabilities.js';
import { canonicalCondition, equationRowKey, } from './equation.js';
import { isGraphEvent, nodesBefore, rowIssues, } from './equation-rules.js';
import { TRANSFER_EVENT_LABELS, TRANSFER_NODE_EVENTS } from './transfer-node.js';
import { GRAPH_EVENT_REGISTRY, COMPLETING_NODE_TYPES, RUNTIME_EVENTS, isEntryEdge, graphSchema, } from './graph.js';
const ROUTABLE_TRANSFER_LABELS = TRANSFER_NODE_EVENTS.map(event => TRANSFER_EVENT_LABELS[event]).join(', ');
function transferLabel(event) {
    return event in TRANSFER_EVENT_LABELS
        ? TRANSFER_EVENT_LABELS[event]
        : event;
}
/**
 * Shape and references in one door. For a caller that only needs to know
 * whether it holds a graph it can run, such as one reading a dispatch body or
 * a shared link. A caller that reports mistakes to the person who made them
 * runs the schema and `validateGraph` apart, so it can say which failed.
 */
export function parseGraph(value) {
    const result = graphSchema.safeParse(value);
    if (!result.success)
        return null;
    return validateGraph(result.data).length === 0 ? result.data : null;
}
export function validateGraph(graph, context = {}) {
    const issues = [];
    const push = (code, message, ref) => issues.push({ code, message, ref });
    const nodeIds = new Set();
    for (const node of graph.nodes) {
        if (nodeIds.has(node.id)) {
            push('duplicate_node_id', `node id "${node.id}" is used twice`, node.id);
        }
        nodeIds.add(node.id);
    }
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
    let entries = 0;
    /** What the entry edges lead to: the Start nodes the call can begin at. */
    const entryTargets = new Set();
    const edgeIds = new Set();
    const exitsFrom = new Map();
    // Conditions already used by an edge leaving the same place. Two edges that
    // open together have no answer: drawn order decides, silently.
    const conditionsFrom = new Map();
    const pairs = new Set();
    // A tool's event needs one of the tools that raise it switched on: on the
    // node the edge leaves, or anywhere in the graph for an edge from anywhere.
    // An action-outcome event needs an action node of a matching type as the
    // owner, and a transfer event a transfer node. A transfer lifecycle marker
    // never routes. A scratchpad event needs the graph to watch for it, on any
    // edge: the runtime listens only for the events the graph watches.
    const reachEvent = (event, edge, owners = ownersOf(edge, graph, nodeById)) => {
        const entry = GRAPH_EVENT_REGISTRY.get(event);
        if (entry?.origin === 'tool') {
            const has = owners.some(n => (n.type === 'conversation' || n.type === 'form') &&
                n.tools.some(t => entry.tools.includes(t.tool)));
            if (!has) {
                push('unreachable_edge', `"${event}" needs one of ${entry.tools.join(' or ')} on ${edge.fromNodeId == null ? 'some node' : 'the node it leaves'}`, edge.id);
            }
        }
        else if (entry?.origin === 'action') {
            const has = owners.some(n => n.type === 'action' && entry.actionTypes.includes(n.actionType));
            if (!has) {
                push('unreachable_edge', `"${event}" needs an action node of type ${entry.actionTypes.join(' or ')} ${edge.fromNodeId == null
                    ? 'somewhere in the graph'
                    : 'as the node it leaves'}`, edge.id);
            }
        }
        else if (entry?.origin === 'transfer') {
            const has = owners.some(n => n.type === 'transfer');
            if (!has) {
                push('unreachable_edge', `"${event}" needs a transfer node ${edge.fromNodeId == null
                    ? 'somewhere in the graph'
                    : 'as the node it leaves'}`, edge.id);
            }
        }
        else if (entry?.origin === 'transfer-audit') {
            push('non_routable_event', `"${transferLabel(event)}" is not an outcome you can route on. Use ${ROUTABLE_TRANSFER_LABELS}.`, edge.id);
        }
        else if (entry?.origin === 'scratchpad' &&
            !graph.watchEvents.includes(event)) {
            push('unreachable_edge', `the graph does not watch for "${event}"`, edge.id);
        }
    };
    const addExit = (from, label) => {
        const set = exitsFrom.get(from) ?? new Set();
        set.add(label);
        exitsFrom.set(from, set);
    };
    for (const edge of graph.edges) {
        if (edgeIds.has(edge.id)) {
            push('duplicate_edge_id', `edge id "${edge.id}" is used twice`, edge.id);
        }
        edgeIds.add(edge.id);
        if (edge.fromNodeId != null && !nodeById.has(edge.fromNodeId)) {
            push('edge_from_missing_node', 'a connection leaves a node that does not exist', edge.id);
        }
        if (edge.toNodeId != null && !nodeById.has(edge.toNodeId)) {
            push('edge_to_missing_node', 'a connection points at a node that does not exist', edge.id);
        }
        const conditionLabel = conditionKey(edge.condition, edge.fromNodeId);
        if (edge.condition.type === 'event') {
            const event = edge.condition.event;
            if (!GRAPH_EVENT_REGISTRY.has(event)) {
                push('unknown_event', `"${event}" is not an event the platform raises`, edge.id);
                continue;
            }
            if (isEntryEdge(edge)) {
                entries += 1;
                if (edge.fromNodeId != null || edge.toNodeId == null) {
                    push('entry_shape', 'the starting connection comes from nowhere and leads to a node', edge.id);
                }
                else {
                    // The call begins at a start_conversation node, which is where the
                    // author says how the conversation opens.
                    entryTargets.add(edge.toNodeId);
                    if (nodeById.get(edge.toNodeId)?.type !== 'start_conversation') {
                        push('entry_shape', 'the entry edge leads to a start_conversation node', edge.id);
                    }
                }
            }
            if (edge.fromNodeId != null)
                addExit(edge.fromNodeId, event);
            reachEvent(event, edge);
            // A completed transfer is the end of the call: the merged leg has
            // ended and there is nobody for the agent to speak with.
            const target = edge.toNodeId == null ? null : nodeById.get(edge.toNodeId);
            if (event === 'transfer_completed' &&
                edge.toNodeId != null &&
                target?.type !== 'end_conversation') {
                push('transfer_completed_must_end', `Transfer success ends the call, so it must connect to an End node. It connects to "${target?.title ?? edge.toNodeId}" now.`, edge.id);
            }
        }
        else if (edge.condition.type === 'equation') {
            const condition = edge.condition;
            if (condition.rows.length === 0) {
                // No condition: the call moves on once the node completes.
                const from = edge.fromNodeId == null ? undefined : nodeById.get(edge.fromNodeId);
                if (!from || !COMPLETING_NODE_TYPES.includes(from.type)) {
                    push('needs_condition', 'only a connection out of a say, transfer or action node can have no condition', edge.id);
                }
            }
            let before;
            const context = {
                nodeById,
                before: () => (before ??= new Set(nodesBefore({ nodes: graph.nodes, edges: graph.edges }, edge).keys())),
            };
            const seenRows = new Set();
            for (const row of condition.rows) {
                const key = equationRowKey(ownRow(row, edge.fromNodeId));
                if (seenRows.has(key)) {
                    push('duplicate_row', 'the equation has the same row twice', edge.id);
                }
                seenRows.add(key);
                if (row.kind !== 'event') {
                    for (const issue of rowIssues(row, edge, context)) {
                        push(issue.code, issue.message, edge.id);
                    }
                    continue;
                }
                if (!GRAPH_EVENT_REGISTRY.has(row.event)) {
                    push('unknown_event', `"${row.event}" is not an event the platform raises`, edge.id);
                    continue;
                }
                if (row.event === RUNTIME_EVENTS.SESSION_STARTED) {
                    push('entry_shape', 'only the starting connection waits for session_started', edge.id);
                    continue;
                }
                if (row.nodeId === undefined) {
                    reachEvent(row.event, edge);
                    continue;
                }
                const node = nodeById.get(row.nodeId);
                if (isGraphEvent(row.event)) {
                    push('graph_event_with_node', `"${row.event}" belongs to the graph, not to a node`, edge.id);
                }
                else if (!node) {
                    push('unknown_reference', `"${row.event}" names a node the graph does not have`, edge.id);
                }
                else if (!context.before().has(node.id)) {
                    push('unreachable_reference', `"${row.event}" from "${node.title}" cannot happen here: the call never passes that node before this connection`, edge.id);
                }
                else {
                    reachEvent(row.event, edge, [node]);
                }
            }
            // An edge from anywhere is checked in every node; without an event it
            // holds as soon as its values do, wherever the call is.
            if (edge.fromNodeId == null && condition.rows.length > 0) {
                const events = condition.rows.filter(row => row.kind === 'event').length;
                const waits = condition.mode === 'all'
                    ? events > 0
                    : events === condition.rows.length;
                if (!waits) {
                    push('cross_cutting_without_event', condition.mode === 'all'
                        ? 'a connection from anywhere needs at least one event to wait for'
                        : 'every condition of an "any" connection from anywhere must be an event', edge.id);
                }
            }
            if (edge.fromNodeId != null)
                addExit(edge.fromNodeId, conditionLabel);
        }
        // Edges from nowhere are grouped together: they are all in scope at once,
        // so two of them on one condition is the same ambiguity.
        const from = edge.fromNodeId ?? '';
        const used = conditionsFrom.get(from) ?? new Set();
        if (used.has(conditionLabel)) {
            push('duplicate_condition', edge.fromNodeId == null
                ? 'two connections from anywhere wait for the same condition'
                : `two connections out of "${nodeById.get(edge.fromNodeId)?.title ?? edge.fromNodeId}" wait for the same condition`, edge.id);
        }
        used.add(conditionLabel);
        conditionsFrom.set(from, used);
        // One edge between two nodes: several ways from one node to another are
        // rows of one equation.
        if (edge.fromNodeId != null) {
            const pair = `${edge.fromNodeId}->${edge.toNodeId ?? ''}`;
            if (pairs.has(pair)) {
                push('parallel_edge', 'two connections join the same two nodes; make them rows of one equation', edge.id);
            }
            pairs.add(pair);
        }
    }
    if (entries === 0) {
        push('entry_count', `exactly one connection must start the flow on ${RUNTIME_EVENTS.SESSION_STARTED}; found ${entries}`);
    }
    // Every conversation must be able to begin. Written as reachability rather
    // than as a count, so a graph that later holds several conversations keeps
    // this rule unchanged: each Start still needs an entry edge of its own.
    const startNodes = graph.nodes.filter(n => n.type === 'start_conversation');
    for (const start of startNodes) {
        if (!entryTargets.has(start.id)) {
            push('start_not_reachable', `node "${start.title}" starts a conversation that nothing begins`, start.id);
        }
    }
    // What each Start opens on, and which Starts reach each node. Both feed the
    // channel rules in the node loop below.
    const startChannels = new Map(startNodes.map(start => [
        start.id,
        start.type === 'start_conversation' ? start.channels : [],
    ]));
    const reachedByStart = startsReaching(graph);
    // A conversation happens one way, so a Start opens on one channel.
    for (const start of startNodes) {
        if (start.type !== 'start_conversation')
            continue;
        if (!spansOneChannelChoice(start.channels)) {
            push('channel_not_one_conversation', `"${start.title}" opens on more than one channel. A Start opens on one: phone, email or SMS.`, start.id);
        }
    }
    // A Start can only open on a channel its trigger can reach: the trigger
    // names the event field holding the destination, and there is nowhere to
    // send without one. Checked only when the caller passed the trigger; the
    // editor validates without one and the API, which holds it, does not.
    const offered = context.trigger
        ? channelsForTriggerVariables(context.trigger)
        : null;
    if (offered) {
        for (const start of startNodes) {
            if (start.type !== 'start_conversation')
                continue;
            const unreachable = start.channels.filter(channel => !offered.includes(channel));
            if (unreachable.length > 0) {
                push('channel_not_offered_by_trigger', `"${start.title}" opens on ${unreachable.join(', ')}, but this graph's trigger has no way to reach someone there`, start.id);
            }
        }
    }
    // The variable a Start sends to must still be on the trigger, and must
    // still carry a type the chosen channel can use. A trigger's variables are
    // edited after a graph binds to it, so a rename or a retype leaves the
    // graph pointing at nothing — caught here rather than at dispatch, where
    // the only symptom is a call that never goes out.
    if (context.trigger) {
        const trigger = context.trigger;
        for (const start of startNodes) {
            if (start.type !== 'start_conversation')
                continue;
            const variable = start.destinationVariable;
            if (!variable)
                continue;
            const usable = start.channels.every(channel => destinationVariablesFor(trigger, channel === 'EMAIL' ? 'email' : 'phone').includes(variable));
            if (!usable) {
                push('destination_variable_not_on_trigger', `"${start.title}" sends to {${variable}}, which this graph's trigger no longer declares for ${start.channels.join(', ')}`, start.id);
            }
        }
    }
    // One conversation per graph, for now. Sequences lift this on their own by
    // deleting this rule; nothing else depends on the count.
    if (startNodes.length > 1) {
        for (const extra of startNodes.slice(1)) {
            push('single_conversation_graph', 'a graph holds one conversation, so it has one start_conversation node', extra.id);
        }
    }
    const endNodes = graph.nodes.filter(n => n.type === 'end_conversation');
    if (endNodes.length === 0) {
        push('missing_end_conversation', 'the flow needs at least one End node');
    }
    for (const node of graph.nodes) {
        // An item or signal id is what edges and events name; two on one node
        // would make both ambiguous.
        const seen = new Set();
        for (const id of itemIdsOf(node)) {
            if (seen.has(id)) {
                push('duplicate_item_id', `node "${node.title}" uses "${id}" twice`, node.id);
            }
            seen.add(id);
        }
        // A node reads a knowledge base or it does not. Asking for both is a row
        // that cannot be stored, so it is caught before anyone tries.
        if (node.type === 'conversation') {
            const excluded = new Set(node.excludedKnowledgeBaseIds);
            for (const id of node.knowledgeBaseIds) {
                if (excluded.has(id)) {
                    push('knowledge_added_and_excluded', `node "${node.id}" both adds and turns off knowledge base "${id}"`, node.id);
                }
            }
        }
        if (node.type === 'say' && !exitsFrom.has(node.id)) {
            push('say_without_exit', `"${node.title}" has nowhere to go after it speaks. Connect it to the next node.`, node.id);
        }
        if (node.type === 'form' && !exitsFrom.has(node.id)) {
            push('form_without_exit', `"${node.title}" has nowhere to go when the form completes. Connect it to the next node.`, node.id);
        }
        else if (node.type === 'form' &&
            !graph.edges.some(edge => edge.fromNodeId === node.id &&
                holdsOnceFinished(edge.condition, node.id))) {
            push('form_without_fallback', `"${node.title}" needs one connection that always holds once it finishes: one on "Form completed" alone, or "any" with "Form completed" in it`, node.id);
        }
        if (node.type === 'transfer' && !exitsFrom.has(node.id)) {
            push('transfer_without_exit', `"${node.title}" has nowhere to go after the transfer. Connect it to the next node, with no condition or on an outcome such as ${TRANSFER_EVENT_LABELS.transfer_failed}.`, node.id);
        }
        // A node must work on every channel that can reach it. Checked per Start
        // rather than per graph: a graph holding several conversations has no one
        // channel, and this rule already reads the way it will need to then.
        const reachingStarts = reachedByStart.get(node.id);
        if (reachingStarts) {
            const channels = [...reachingStarts].flatMap(startId => startChannels.get(startId) ?? []);
            const unsupported = channels.filter(channel => !nodeTypeRunsOn(node.type, [channel]));
            if (unsupported.length > 0) {
                push('node_type_not_on_channel', `"${node.title}" is a ${node.type} step, which does not work on ${[
                    ...new Set(unsupported),
                ].join(', ')}`, node.id);
            }
        }
    }
    // Text channels do not load the system and contact context voice reads a
    // value row from, so such a row never holds there. The author hears it now
    // rather than from an edge that never fires.
    const allStartChannels = [...startChannels.values()].flat();
    for (const edge of graph.edges) {
        if (edge.condition.type !== 'equation')
            continue;
        const channels = edge.fromNodeId == null
            ? allStartChannels
            : [...(reachedByStart.get(edge.fromNodeId) ?? [])].flatMap(startId => startChannels.get(startId) ?? []);
        const textChannels = [
            ...new Set(channels.filter(channel => channel !== 'CALL')),
        ];
        if (textChannels.length === 0)
            continue;
        for (const row of edge.condition.rows) {
            if (row.kind !== 'value')
                continue;
            if (row.ref.source !== 'system' && row.ref.source !== 'contact')
                continue;
            push('row_source_not_on_channel', `a connection reads {${row.ref.field}}, which is not available on ${textChannels.join(', ')} yet`, edge.id);
        }
    }
    return issues;
}
/** Whether the condition holds once the form completes, whatever its answers. */
function holdsOnceFinished(condition, formId) {
    if (condition.type !== 'equation')
        return false;
    const completed = condition.rows.some(row => row.kind === 'result' &&
        row.source === 'form' &&
        row.nodeId === formId &&
        row.is === 'completed');
    return completed && (condition.mode === 'any' || condition.rows.length === 1);
}
/**
 * On an edge out of a node, an event row that names that node is the node's
 * own event, so the row drops the name.
 */
function ownRow(row, fromNodeId) {
    return row.kind === 'event' && row.nodeId === fromNodeId
        ? { kind: row.kind, event: row.event }
        : row;
}
/**
 * What makes two conditions the same: their canonical form, with an
 * equation's rows in any order, each row once, and a node's own events
 * without the node's name.
 */
function conditionKey(condition, fromNodeId) {
    const own = condition.type === 'equation'
        ? {
            ...condition,
            rows: [
                ...new Map(condition.rows
                    .map(row => ownRow(row, fromNodeId))
                    .map(row => [equationRowKey(row), row])).values(),
            ],
        }
        : condition;
    const canonical = canonicalCondition(own);
    if (canonical.type === 'event')
        return `event:${canonical.event}`;
    return `equation:${canonical.mode}:${canonical.rows.map(equationRowKey).sort().join('+')}`;
}
/**
 * What a node listens for. Only a conversation node has signals; read by
 * property presence rather than by `type`, so it holds in every compilation
 * context, including ones where the node union is seen with optional keys.
 */
function signalsOf(node) {
    return 'signals' in node ? (node.signals ?? []) : [];
}
/** The ids a node's own rows carry: a form's items, a conversation node's signals. */
function itemIdsOf(node) {
    if ('items' in node)
        return node.items.map(item => item.id);
    return signalsOf(node).map(signal => signal.id);
}
function ownersOf(edge, graph, nodeById) {
    if (edge.fromNodeId == null)
        return graph.nodes;
    const from = nodeById.get(edge.fromNodeId);
    return from ? [from] : [];
}
//# sourceMappingURL=validate.js.map